import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Modal,
  Tooltip,
  CircularProgress,
  MenuItem,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Switch,
  FormControlLabel,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import AddIcon from '@mui/icons-material/Add';
import { Editor } from '@monaco-editor/react';
import {
  CRAFTERHACK_DARK_THEME,
  CRAFTERHACK_LIGHT_THEME,
  defineCrafterhackThemes,
  getCrafterhackEditorOptions,
} from '../common/monacoAppTheme';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import VideocamIcon from '@mui/icons-material/Videocam';
import CodeIcon from '@mui/icons-material/Code';
import { alpha } from '@mui/material/styles';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import {
  COMPILER_LANGUAGES,
  defaultCode,
  getCompilerLanguage,
  getMonacoLanguage,
  isCompilerLanguage,
  languageMapping,
} from '../common/compilerLanguages';
import proxy from '../api/axios';
import MentorshipCallout from '../components/MentorshipCallout';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import IdeConsolePanel from '../components/IdeConsolePanel';
import CompilerLanguagePicker from '../components/CompilerLanguagePicker';
import IdeLanguageSeoSection from '../components/IdeLanguageSeoSection';
import { getIdeLanguageSeo } from '../common/ideLanguageSeo';
import {
  areShortcutsEnabled,
  setShortcutsEnabled,
  matchProblemShortcut,
  IDE_SHORTCUTS,
} from '../components/problems/problemShortcuts';

const ACCENT = '#137274';

const readInitialLanguage = (routeLang, searchLang) => {
  if (routeLang && isCompilerLanguage(routeLang)) return routeLang;
  if (searchLang && isCompilerLanguage(searchLang)) return searchLang;
  const stored = localStorage.getItem('lang');
  if (stored && isCompilerLanguage(stored)) return stored;
  return 'python';
};

// Same "Solutions" chip system as the problem page — multiple named code
// buffers per language, switchable via chips above the editor.
const solutionsStorageKey = (lang) => `ide-solutions-${lang}`;
const activeSolutionStorageKey = (lang) => `ide-active-solution-${lang}`;

const persistSolutions = (list, lang) => {
  try {
    localStorage.setItem(solutionsStorageKey(lang), JSON.stringify(list));
  } catch {
    /* ignore */
  }
};

const persistActiveSolutionId = (id, lang) => {
  try {
    localStorage.setItem(activeSolutionStorageKey(lang), id);
  } catch {
    /* ignore */
  }
};

// Loads (or migrates the legacy single-buffer `compiler-code-<lang>` key
// into) the "Solutions" chip list for a language.
const loadSolutions = (lang) => {
  let stored = null;
  try {
    stored = JSON.parse(localStorage.getItem(solutionsStorageKey(lang)) || 'null');
  } catch {
    stored = null;
  }
  if (Array.isArray(stored) && stored.length) {
    const storedActiveId = localStorage.getItem(activeSolutionStorageKey(lang));
    const active = stored.find((s) => s.id === storedActiveId) || stored[0];
    return { list: stored, activeId: active.id };
  }
  const legacyCode = localStorage.getItem(`compiler-code-${lang}`) || defaultCode[lang];
  const list = [{ id: 'sol-1', label: 'Solution 1', code: legacyCode }];
  return { list, activeId: list[0].id };
};

const CompilerPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appTheme = useTheme();
  const isDark = appTheme.palette.mode === 'dark';
  const isMobile = useMediaQuery(appTheme.breakpoints.down('md'));
  const { link } = useParams();
  const shareLinkId = link && !isCompilerLanguage(link) ? link : null;
  const isLanguageLanding = Boolean(link && isCompilerLanguage(link));

  const [language, setLanguage] = useState(() =>
    readInitialLanguage(
      link && isCompilerLanguage(link) ? link : null,
      searchParams.get('lang'),
    ),
  );
  const [solutions, setSolutions] = useState(() => loadSolutions(language).list);
  const [activeSolutionId, setActiveSolutionId] = useState(
    () => loadSolutions(language).activeId,
  );
  const [deleteSolutionId, setDeleteSolutionId] = useState(null);
  const [code, setCode] = useState(() => {
    const { list, activeId } = loadSolutions(language);
    return (list.find((s) => s.id === activeId) || list[0]).code;
  });
  const [customInput, setCustomInput] = useState('');
  const [customCases, setCustomCases] = useState([]);
  const [selectedCustomIndex, setSelectedCustomIndex] = useState(0);
  const [useCustomInput, setUseCustomInput] = useState(false);
  const [output, setOutput] = useState('');
  const [error, setError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isCompiling, setIsCompiling] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [shortcutsOn, setShortcutsOn] = useState(areShortcutsEnabled);
  const [fontSize, setFontSize] = useState(14);
  const [theme, setTheme] = useState(
    isDark ? CRAFTERHACK_DARK_THEME : CRAFTERHACK_LIGHT_THEME,
  );
  useEffect(() => {
    setTheme(isDark ? CRAFTERHACK_DARK_THEME : CRAFTERHACK_LIGHT_THEME);
  }, [isDark]);
  const monacoOptions = useMemo(
    () => ({
      ...getCrafterhackEditorOptions(fontSize),
      selectOnLineNumbers: true,
    }),
    [fontSize],
  );
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');
  const [justCopied, setJustCopied] = useState(false);
  const [isSharingCode, setIsSharingCode] = useState(false);
  const [codeLink, setCodeLink] = useState(`${window.location.origin}/ide`);
  const [consoleCollapsed, setConsoleCollapsed] = useState(() => {
    try {
      return localStorage.getItem('ide-console-collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const surface = isDark ? '#161b22' : '#ffffff';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 45%, #ffffff 100%)';
  const muted = isDark ? '#8b949e' : '#5f6b73';

  const applySolutions = (lang) => {
    const { list, activeId } = loadSolutions(lang);
    setSolutions(list);
    setActiveSolutionId(activeId);
    setCode((list.find((s) => s.id === activeId) || list[0]).code);
  };

  useEffect(() => {
    const fetchCode = async () => {
      try {
        const { data } = await proxy.get(`/compiler-codes/${shareLinkId}`);
        // Shared code loads into the currently active solution buffer, same
        // as typing would — it stays part of the Solutions chip list.
        setCode(data.code);
        setSolutions((prev) => {
          const next = prev.map((s) =>
            s.id === activeSolutionId ? { ...s, code: data.code } : s,
          );
          persistSolutions(next, language);
          return next;
        });
      } catch (err) {
        toast.error('Error fetching code');
        console.error('Error fetching code:', err);
      }
    };
    if (shareLinkId) fetchCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the share id changes
  }, [shareLinkId]);

  useEffect(() => {
    // Canonicalize bare "/ide?lang=x" to "/ide/x" for SEO — but never when
    // `link` is a share code (e.g. "/ide/ab12cd34?lang=java", the exact
    // shape handleShareCode generates). Share links carry ?lang= so the
    // editor picks the right language; without this guard we'd redirect
    // away from the share id before fetchCode ever runs, silently dropping
    // the shared code and swapping in the blank language template instead.
    if (shareLinkId) return;
    const langParam = searchParams.get('lang');
    if (langParam && isCompilerLanguage(langParam) && link !== langParam) {
      navigate(`/ide/${langParam}`, { replace: true });
    }
  }, [searchParams, link, navigate, shareLinkId]);

  useEffect(() => {
    if (!isLanguageLanding || link === language) return;
    setLanguage(link);
    applySolutions(link);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applySolutions is stable per render, not a dep source
  }, [link, isLanguageLanding, language]);

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isFullscreen]);

  useEffect(() => {
    localStorage.setItem('ide-console-collapsed', String(consoleCollapsed));
  }, [consoleCollapsed]);

  const handleRunCode = async () => {
    setOutput('');
    setError(false);
    setConsoleCollapsed(false);
    setIsCompiling(true);
    try {
      const effectiveInput = useCustomInput
        ? customCases[selectedCustomIndex]?.input ?? ''
        : customInput;
      const requestBody = {
        source_code: code,
        language_id: languageMapping[language],
        testCase: { input: effectiveInput },
      };
      const { data } = await proxy.post('/code/compile', requestBody, {
        headers: { 'Content-Type': 'application/json' },
      });
      setOutput(data.message);
      setError(data.status !== 'Accepted');
    } catch (err) {
      console.error('Error:', err);
      setError(true);
      setOutput(err?.response?.data?.message || 'Error occurred while running the code.');
    } finally {
      setIsCompiling(false);
    }
  };

  // Same bindings as the problem page (matchProblemShortcut) — the IDE has
  // no submit/maximize concept of its own, so "submit" also runs code and
  // "maximize" falls back to the fullscreen toggle.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (!shortcutsOn) return;
      const action = matchProblemShortcut(event);
      if (!action) return;

      event.preventDefault();
      event.stopPropagation();

      if (action === 'run' || action === 'submit') handleRunCode();
      else if (action === 'close-tab') setConsoleCollapsed((c) => !c);
      else if (action === 'maximize' || action === 'fullscreen') {
        setIsFullscreen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  });

  const selectLanguage = (next) => {
    if (!isCompilerLanguage(next) || next === language) return;
    setLanguage(next);
    applySolutions(next);
    localStorage.setItem('lang', next);
    navigate(`/ide/${next}`, { replace: true });
  };

  const handleCodeChange = (newCode) => {
    const value = newCode || defaultCode[language];
    setCode(value);
    setSolutions((prev) => {
      const next = prev.map((s) =>
        s.id === activeSolutionId ? { ...s, code: value } : s,
      );
      persistSolutions(next, language);
      return next;
    });
  };

  const handleResetCode = () => {
    handleCodeChange(defaultCode[language]);
  };

  const handleSelectSolution = (id) => {
    if (id === activeSolutionId) return;
    const target = solutions.find((s) => s.id === id);
    if (!target) return;
    setActiveSolutionId(id);
    setCode(target.code);
    persistActiveSolutionId(id, language);
  };

  const handleAddSolution = () => {
    const newSolution = {
      id: `sol-${Date.now()}`,
      label: `Solution ${solutions.length + 1}`,
      code: defaultCode[language],
    };
    const next = [...solutions, newSolution];
    setSolutions(next);
    setActiveSolutionId(newSolution.id);
    setCode(newSolution.code);
    persistSolutions(next, language);
    persistActiveSolutionId(newSolution.id, language);
  };

  const requestDeleteSolution = (id) => {
    if (solutions.length <= 1) {
      toast.info('You need at least one solution.');
      return;
    }
    setDeleteSolutionId(id);
  };

  const cancelDeleteSolution = () => setDeleteSolutionId(null);

  const confirmDeleteSolution = () => {
    const id = deleteSolutionId;
    setDeleteSolutionId(null);
    if (!id) return;
    const next = solutions
      .filter((s) => s.id !== id)
      .map((s, i) => ({ ...s, label: `Solution ${i + 1}` }));
    if (!next.length) return;
    setSolutions(next);
    persistSolutions(next, language);
    if (id === activeSolutionId) {
      const nextActive = next[0];
      setActiveSolutionId(nextActive.id);
      setCode(nextActive.code);
      persistActiveSolutionId(nextActive.id, language);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(codeLink);
      setCopySuccess('Link copied!');
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 1500);
    } catch (_) {
      setCopySuccess('Failed to copy the link.');
    }
  };

  const handleShareCode = async () => {
    if (isSharingCode) return;
    setIsSharingCode(true);
    try {
      const { data } = await proxy.post('/compiler-codes', { code });
      const langQuery = language ? `?lang=${encodeURIComponent(language)}` : '';
      setCodeLink(`${window.location.origin}/ide/${data.link}${langQuery}`);
      setJustCopied(false);
      setCopySuccess('');
      setShareModalOpen(true);
    } catch (err) {
      toast.error('Error generating link');
      console.error(err);
    } finally {
      setIsSharingCode(false);
    }
  };

  const toolbar = (
    <Box
      data-tour="ide-toolbar"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.25,
        py: 1,
        borderBottom: `1px solid ${border}`,
        flexWrap: 'wrap',
        bgcolor: surface,
        flexShrink: 0,
      }}
    >
      {isFullscreen && (
        <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
          <CompilerLanguagePicker
            languages={COMPILER_LANGUAGES}
            value={language}
            onChange={selectLanguage}
            size="compact"
          />
        </Box>
      )}

      <Button
        variant="contained"
        size="small"
        onClick={handleRunCode}
        disabled={isCompiling}
        data-tour="ide-run"
        startIcon={
          isCompiling ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />
        }
        sx={{
          textTransform: 'none',
          fontWeight: 800,
          bgcolor: '#1a7f37',
          px: 1.75,
          '&:hover': { bgcolor: '#15632c' },
        }}
      >
        {isCompiling ? 'Running…' : 'Run'}
      </Button>

      <Box sx={{ flex: 1 }} />

      <Tooltip title="Take a quick tour">
        <IconButton
          size="small"
          onClick={() => window.dispatchEvent(new CustomEvent('ch-start-ide-tour'))}
          aria-label="tour"
        >
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Get a shareable link to this code">
        <Button
          size="small"
          variant="outlined"
          onClick={handleShareCode}
          disabled={isSharingCode}
          startIcon={
            isSharingCode ? (
              <CircularProgress size={14} sx={{ color: ACCENT }} />
            ) : (
              <ShareIcon fontSize="small" />
            )
          }
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderColor: alpha(ACCENT, 0.4),
            color: ACCENT,
            px: 1.25,
            '&:hover': { borderColor: ACCENT, bgcolor: alpha(ACCENT, 0.08) },
          }}
        >
          Share
        </Button>
      </Tooltip>
      <Tooltip title="Reset to template">
        <IconButton size="small" onClick={handleResetCode} aria-label="reset">
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Editor settings">
        <IconButton size="small" onClick={() => setOpenSettings(true)} aria-label="settings">
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}>
        <IconButton
          size="small"
          onClick={() => setIsFullscreen((v) => !v)}
          aria-label="fullscreen"
        >
          {isFullscreen ? (
            <CloseFullscreenIcon fontSize="small" />
          ) : (
            <FullscreenIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );

  const consolePanel = (
    <IdeConsolePanel
      dataTour="ide-console"
      customInput={customInput}
      onCustomInputChange={setCustomInput}
      output={output}
      error={error}
      isCompiling={isCompiling}
      collapsed={consoleCollapsed}
      onToggleCollapse={() => setConsoleCollapsed((c) => !c)}
      isFullscreen={isFullscreen}
      border={border}
      muted={muted}
      surface={surface}
      isDark={isDark}
      inputPlaceholder="Enter test case input…"
      useCustomInput={useCustomInput}
      onUseCustomInputChange={setUseCustomInput}
      customCases={customCases}
      selectedCustomIndex={selectedCustomIndex}
      onSelectCustomCase={setSelectedCustomIndex}
      onAddCustomCase={() => {
        const next = {
          id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          input: '',
          args: {},
        };
        setCustomCases((prev) => {
          const list = [...prev, next];
          setSelectedCustomIndex(list.length - 1);
          return list;
        });
        setUseCustomInput(true);
      }}
      onRemoveCustomCase={(index) => {
        setCustomCases((prev) => {
          if (index < 0 || index >= prev.length) return prev;
          const list = prev.filter((_, i) => i !== index);
          if (list.length === 0) {
            setUseCustomInput(false);
            setSelectedCustomIndex(0);
            return list;
          }
          setSelectedCustomIndex((cur) => {
            if (cur === index) return Math.max(0, index - 1);
            if (cur > index) return cur - 1;
            return cur;
          });
          return list;
        });
      }}
      onCustomCaseChange={(index, updater) => {
        setCustomCases((prev) => {
          if (index < 0 || index >= prev.length) return prev;
          const current = prev[index] || { input: '', args: {} };
          const next =
            typeof updater === 'function' ? updater(current) : { ...current, ...updater };
          const list = [...prev];
          list[index] = { id: current.id, input: next.input ?? '', args: next.args || {} };
          return list;
        });
      }}
    />
  );

  const editorPane = (height) => (
    <Box data-tour="ide-editor" sx={{ flex: 1, minHeight: 0, height: '100%' }}>
      <Editor
        height={height}
        width="100%"
        language={getMonacoLanguage(language)}
        value={code}
        theme={theme}
        beforeMount={defineCrafterhackThemes}
        onChange={handleCodeChange}
        options={monacoOptions}
      />
    </Box>
  );

  const solutionsBar = (
    <Box
      data-tour="ide-solutions"
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.75,
        px: 1.25,
        py: 0.85,
        borderBottom: `1px solid ${border}`,
        flexShrink: 0,
      }}
    >
      {solutions.map((solution) => (
        <Chip
          key={solution.id}
          label={solution.label}
          size="small"
          onClick={() => handleSelectSolution(solution.id)}
          onDelete={
            solutions.length > 1 ? () => requestDeleteSolution(solution.id) : undefined
          }
          sx={{
            fontWeight: 700,
            fontSize: '0.72rem',
            bgcolor:
              solution.id === activeSolutionId
                ? ACCENT
                : isDark
                  ? '#21262d'
                  : 'rgba(15,60,70,0.08)',
            color: solution.id === activeSolutionId ? '#fff' : muted,
            '& .MuiChip-deleteIcon': {
              color: solution.id === activeSolutionId ? 'rgba(255,255,255,0.7)' : muted,
              '&:hover': {
                color: solution.id === activeSolutionId ? '#fff' : '#e5534b',
              },
            },
          }}
        />
      ))}
      <Tooltip title="Add a new solution">
        <IconButton onClick={handleAddSolution} size="small" sx={{ color: muted }}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const activeLang = getCompilerLanguage(language);
  const seo = isLanguageLanding ? getIdeLanguageSeo(language) : null;
  const jsonLd = seo
    ? {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: seo.h1,
        description: seo.description,
        url: seo.canonical,
        applicationCategory: 'DeveloperApplication',
        operatingSystem: 'Web',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      }
    : null;

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        background: pageBg,
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 2, md: 2.5 },
      }}
    >
      <Helmet>
        <title>
          {seo ? seo.title : `CrafterHack IDE | Online ${activeLang.label} Compiler`}
        </title>
        <meta
          name="description"
          content={
            seo?.description ||
            `Free online ${activeLang.label} compiler on CrafterHack. Run Python, Java, C++, JavaScript, Go, Rust, TypeScript, and more — write, share, and pair program.`
          }
        />
        {seo?.keywords && <meta name="keywords" content={seo.keywords} />}
        <link
          rel="canonical"
          href={seo ? seo.canonical : 'https://crafterhack.com/ide'}
        />
        {jsonLd && (
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        )}
      </Helmet>

      {/* Top bar */}
      <Box
        sx={{
          maxWidth: 1280,
          mx: 'auto',
          mb: 2,
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <CodeIcon sx={{ color: ACCENT, fontSize: 28 }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.25rem', sm: '1.45rem' },
              }}
            >
              IDE
            </Typography>
            <Chip
              size="small"
              label={activeLang.label}
              sx={{
                height: 22,
                fontWeight: 700,
                bgcolor: alpha(ACCENT, isDark ? 0.25 : 0.1),
                color: ACCENT,
              }}
            />
            <Chip
              size="small"
              label={`${COMPILER_LANGUAGES.length} languages`}
              variant="outlined"
              sx={{
                height: 22,
                fontWeight: 700,
                borderColor: alpha(ACCENT, 0.35),
                color: muted,
              }}
            />
          </Stack>
          <Typography variant="body2" sx={{ color: muted, maxWidth: 520, mb: 1.5 }}>
            Write, run, and share in the browser — switch languages instantly. ⌘/Ctrl+Enter to run.
          </Typography>
          <CompilerLanguagePicker
            languages={COMPILER_LANGUAGES}
            value={language}
            onChange={selectLanguage}
            size="compact"
          />
        </Box>

        <Button
          variant="contained"
          startIcon={<VideocamIcon />}
          onClick={() => navigate('/live-coding')}
          sx={{
            alignSelf: { xs: 'stretch', sm: 'center' },
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: ACCENT,
            px: 2,
            '&:hover': { bgcolor: '#0f5c5e' },
            whiteSpace: 'nowrap',
          }}
        >
          Live Coding Room
        </Button>
      </Box>

      {/* Workspace — normal mode */}
      {!isFullscreen && (
        <Box
          sx={{
            maxWidth: 1280,
            mx: 'auto',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 1.45fr) minmax(280px, 0.85fr)',
            },
            gap: 1.5,
            alignItems: 'stretch',
            mb: 3,
          }}
        >
          <Box
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${border}`,
              bgcolor: surface,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: { xs: 300, md: 'calc(100vh - 220px)' },
              height: { xs: 'min(52dvh, 420px)', md: 'auto' },
              boxShadow: isDark ? 'none' : '0 8px 24px rgba(15, 60, 70, 0.06)',
            }}
          >
            {toolbar}
            {solutionsBar}
            {editorPane(isMobile ? '100%' : 'calc(100vh - 280px)')}
          </Box>

          <Box
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${border}`,
              overflow: 'hidden',
              minHeight: { xs: 240, md: 'calc(100vh - 220px)' },
              boxShadow: isDark ? 'none' : '0 8px 24px rgba(15, 60, 70, 0.06)',
            }}
          >
            {consolePanel}
          </Box>
        </Box>
      )}

      {/* 1:1 Mentorship */}
      {!isFullscreen && (
        <Box sx={{ maxWidth: 1280, mx: 'auto', mb: 2 }}>
          <MentorshipCallout variant="banner" />
        </Box>
      )}

      {isLanguageLanding && !isFullscreen && (
        <IdeLanguageSeoSection languageId={language} />
      )}

      {/* Fullscreen overlay — editor + run + console */}
      {isFullscreen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            bgcolor: isDark ? '#0d1117' : '#f4f8f9',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {toolbar}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.55fr) minmax(300px, 0.9fr)' },
              gridTemplateRows: { xs: 'minmax(0, 1fr) minmax(240px, 0.55fr)', md: '1fr' },
            }}
          >
            <Box
              sx={{
                minHeight: 0,
                borderRight: { md: `1px solid ${border}` },
                borderBottom: { xs: `1px solid ${border}`, md: 'none' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {solutionsBar}
              {editorPane('100%')}
            </Box>
            <Box sx={{ minHeight: 0, overflow: 'hidden' }}>{consolePanel}</Box>
          </Box>
        </Box>
      )}

      {/* Share modal */}
      <Modal
        open={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          setCopySuccess('');
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '92%', sm: 420 },
            bgcolor: surface,
            border: `1px solid ${border}`,
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            zIndex: 1500,
          }}
        >
          <Typography variant="h6" fontWeight={800} gutterBottom>
            Share code link
          </Typography>
          <Typography variant="body2" sx={{ color: muted, mb: 2 }}>
            Anyone with this link can open your code. Links stay active for 1 year.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.25,
              borderRadius: 1.5,
              bgcolor: isDark ? '#0d1117' : '#f6f8fa',
              border: `1px solid ${border}`,
              mb: 1.5,
              wordBreak: 'break-all',
            }}
          >
            <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace', fontSize: 12 }}>
              {codeLink}
            </Typography>
            <IconButton
              color={justCopied ? 'success' : 'primary'}
              onClick={handleCopyLink}
              size="small"
            >
              {justCopied ? (
                <CheckIcon fontSize="small" />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
          {copySuccess && (
            <Typography variant="body2" color="success.main" sx={{ mb: 1.5 }}>
              {copySuccess}
            </Typography>
          )}
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setShareModalOpen(false);
              setCopySuccess('');
            }}
            startIcon={<CloseIcon />}
            sx={{ textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: '#0f5c5e' } }}
          >
            Close
          </Button>
        </Box>
      </Modal>

      {/* Settings modal */}
      <Modal open={openSettings} onClose={() => setOpenSettings(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '92%', sm: 400 },
            bgcolor: surface,
            border: `1px solid ${border}`,
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            zIndex: 1500,
          }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
            Editor settings
          </Typography>
          <TextField
            select
            label="Theme"
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            <MenuItem value={CRAFTERHACK_DARK_THEME}>Dark</MenuItem>
            <MenuItem value={CRAFTERHACK_LIGHT_THEME}>Light</MenuItem>
          </TextField>
          <TextField
            select
            label="Font size"
            value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
            fullWidth
            sx={{ mb: 2 }}
          >
            {[12, 13, 14, 16, 18, 20].map((size) => (
              <MenuItem key={size} value={size}>
                {size}px
              </MenuItem>
            ))}
          </TextField>

          <Box
            sx={{
              border: `1px solid ${border}`,
              borderRadius: 2,
              bgcolor: isDark ? alpha('#fff', 0.03) : '#fafbfc',
              px: 1.5,
              py: 1.15,
              mb: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
                Keyboard shortcuts
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: muted, mt: 0.25 }}>
                Shortcuts work even inside the code editor.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={shortcutsOn}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setShortcutsOn(on);
                    setShortcutsEnabled(on);
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: muted }}>
                  Enabled
                </Typography>
              }
              sx={{ mr: 0, flexShrink: 0 }}
            />
          </Box>

          <Box
            sx={{
              border: `1px solid ${border}`,
              borderRadius: 2,
              overflow: 'hidden',
              opacity: shortcutsOn ? 1 : 0.55,
              mb: 2,
            }}
          >
            {IDE_SHORTCUTS.map((row, i) => (
              <Box
                key={row.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  px: 1.5,
                  py: 1.15,
                  borderTop: i === 0 ? 'none' : `1px solid ${border}`,
                  bgcolor: isDark ? alpha('#fff', 0.02) : '#fafbfc',
                }}
              >
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  {row.action}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {row.keys().map((k, ki) => (
                    <React.Fragment key={`${row.id}-${ki}`}>
                      {ki > 0 && (
                        <Typography
                          component="span"
                          sx={{ fontSize: '0.7rem', color: muted, fontWeight: 700 }}
                        >
                          +
                        </Typography>
                      )}
                      <Box
                        component="kbd"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 26,
                          height: 24,
                          px: 0.75,
                          borderRadius: 1,
                          border: `1px solid ${border}`,
                          bgcolor: isDark ? '#0d1117' : '#fff',
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          color: isDark ? '#e6edf3' : '#0f2a2b',
                          boxShadow: isDark ? 'none' : '0 1px 0 rgba(15,60,70,0.08)',
                        }}
                      >
                        {k}
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={() => setOpenSettings(false)}
            sx={{ textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: '#0f5c5e' } }}
          >
            Done
          </Button>
        </Box>
      </Modal>

      {/* Delete solution confirmation */}
      <Modal open={Boolean(deleteSolutionId)} onClose={cancelDeleteSolution}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '92%', sm: 380 },
            bgcolor: surface,
            border: `1px solid ${border}`,
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            zIndex: 1500,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.02em' }}>
            Delete this solution?
          </Typography>
          <Typography variant="body2" sx={{ color: muted, mb: 3, fontSize: '0.85rem' }}>
            {solutions.find((s) => s.id === deleteSolutionId)?.label} and its code will be
            permanently removed. This can't be undone.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={cancelDeleteSolution} sx={{ textTransform: 'none', fontWeight: 700 }}>
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteSolution}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: '#e5534b',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#c8433c', boxShadow: 'none' },
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default CompilerPage;
